import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ReactGA from "react-ga4";
import { ALL_RUN_CLUB_DATA } from "../assets/runClubDataIndex.js";
import { ALL_CLUB_PROFILES } from "../assets/clubProfilesIndex.js";
import "./ClubProfile.css";
import BackButton from './backbutton/BackButton';
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LanguageIcon from "@mui/icons-material/Language";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStrava } from "@fortawesome/free-brands-svg-icons";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import InfoIcon from "@mui/icons-material/Info";
import ListIcon from "@mui/icons-material/List";
import { UserPlus } from 'lucide-react';
import { Helmet } from 'react-helmet';
import ClubMapRun from './ClubMapRun';
import ShareClub from "./ShareClub";
import EmailModal from './EmailModal';
import { Users, Activity, Coffee, DollarSign, Calendar } from 'lucide-react';
import { sanitizeHtml } from '../utils/sanitizeHtml';

function ClubProfile() {

  const { city, id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [showEmailModal, setShowEmailModal] = useState(false);

  // Get basic club data from ALL_RUN_CLUB_DATA
  const basicClubData = ALL_RUN_CLUB_DATA.find(club => club.id === id);
  // Get extended club profile data from ALL_CLUB_PROFILES
  const extendedClubData = ALL_CLUB_PROFILES[id];
  
  // Merge the data, with extended data taking precedence
  const club = {
    ...basicClubData,
    ...extendedClubData
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (club) {
      const gaEventData = {
        category: "User",
        action: "club_profile_click",
        label: club.title,
      };
      ReactGA.event(gaEventData);
      console.log("Data sent to Google Analytics:", gaEventData);
    }

    // Load Instagram embed script
    const instagramScript = document.createElement('script');
    instagramScript.src = '//www.instagram.com/embed.js';
    instagramScript.async = true;
    document.body.appendChild(instagramScript);

    // Load Reddit embed script
    const redditScript = document.createElement('script');
    redditScript.src = 'https://embed.reddit.com/widgets.js';
    redditScript.async = true;
    redditScript.charset = 'UTF-8';

    document.body.appendChild(redditScript);

    return () => {
      document.body.removeChild(instagramScript);
      document.body.removeChild(redditScript);
    };
  }, [id]);

  useEffect(() => {
    if (window.instgrm) {
      window.instgrm.Embeds.process();
    }
    setIsLoading(false);
  }, []);

  if (!club) {
    return <h2>Club not found</h2>;
  }

  const contactIcons = {
    instagram: <InstagramIcon className="club-icon" />,
    facebook: <FacebookIcon className="club-icon" />,
    whatsapp: <WhatsAppIcon className="club-icon" />,
    website: <LanguageIcon className="club-icon" />,
    strava: <FontAwesomeIcon icon={faStrava} className="club-icon" />
  };

  const runInfoIcons = {
    day: <CalendarTodayIcon className="run-icon" />,
    time: <AccessTimeIcon className="run-icon" />,
    distance: <DirectionsRunIcon className="run-icon" />,
    location: <LocationOnIcon className="run-icon" />,
    workoutType: <ListIcon className="run-icon" />,
    details: <InfoIcon className="run-icon" />
  };

  const handleJoinClubClick = () => {
    ReactGA.event({
      category: "User",
      action: "Join Club",
      label: club.title,
      value: id,
    });
  };

  const handleViewRouteClick = (routeLink) => {
    window.open(routeLink, '_blank');
  };

  const handleModalClose = () => {
    setShowEmailModal(false);
  };

  // Create location data object
  const locationData = {
    city: city,
    clubName: club?.title || 'Unknown Club',
    currentUrl: window.location.href,
    clubId: id,
    clubSubtitle: club?.subtitle || ''
  };

  return (
    <div className="club-profile">
      <Helmet>
        <title>{club.title} | Club Profile</title>
        <meta name="description" content={club.description} />
      </Helmet>
      
      {/* Email Modal - Updated to pass locationData */}
      
      <EmailModal 
        show={showEmailModal} 
        onClose={handleModalClose} 
        locationData={locationData} 
      />

      {/* All existing club profile content - greyed out when modal is showing */}
      <div style={{ 
        filter: showEmailModal ? 'grayscale(1) blur(1px)' : 'none',
        pointerEvents: showEmailModal ? 'none' : 'auto',
        transition: 'filter 0.3s ease'
      }}>
        <div className="club-header">
          <BackButton />
        </div>
        <ShareClub
          clubUrl={`https://www.clubsta.co/runclubs/${city.toLowerCase()}/${id}`}
          clubTitle={club.title}
        />
        <h1 className="club-title">{club.title}</h1>
        <div className="club-main-layout-wrapper">
          <div className="club-images">
          
            <img className="club-image" src={club.images[0]}/>
            
            <div className="club-subtitle">{club.subtitle}</div>

              <div className="club-links">
                <ul>
                  {club.contactInfo.filter(item => item.url).map((item, index) => (
                    <li key={index} className="contact-detail-item">
                      <a href={item.url} target="_blank" rel="noopener noreferrer">
                        {contactIcons[item.platform]}
                        <span className="detail-value">
                          {item.platform.charAt(0).toUpperCase() + item.platform.slice(1)}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>

              </div>
              {(club.ageRange && club.averageAttendance) && (
                <div className="club-stats">
                  <div className="stat-card">
                    <div className="stat-icon">
                      <Users size={32} />
                    </div>
                    <div className="stat-content">
                      <div className="stat-label">Age Range</div>
                      <div className="stat-value">{club.ageRange}</div>
                    </div>
                  </div>
                  
                  <div className="stat-card">
                    <div className="stat-icon">
                      <Activity size={32} />
                    </div>
                    <div className="stat-content">
                      <div className="stat-label">Average Attendance</div>
                      <div className="stat-value">{club.averageAttendance}</div>
                    </div>
                  </div>
                </div>
              )}


            {/* Instagram Feed */}
            {club.instagramEmbed && (
              <section className="club-instagram-feed">
                <h2>Vibes</h2>
                <div className="instagram-feed-container">
                  <div
                    className="instagram-feed"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(club.instagramEmbed) }}
                  />
                </div>
              </section>
            )}

            {/* Reddit Discussions */}
            {club.redditEmbed && (
              <section className="club-reddit-feed">
                <h2>What Others Are Saying</h2>
                <div className="reddit-feed-container">
                  <div
                    className="reddit-feed"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(club.redditEmbed) }}
                  />
                </div>
              </section>
            )}

          </div>


        <div className="club-content">
          <main className="club-main">
          {club.joinLink && (
              <a
                href={club.joinLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none' }}
              >
                <button className="join-button" onClick={handleJoinClubClick}>
                  <UserPlus size={18} />
                  <span>Join Club</span>
                </button>
              </a>
            )}
            {club.description && (
              <section className="club-about">
                <h2>About</h2>
                <p>{club.description}</p>
              </section>
            )}

            {club.whatToExpect && (
              <section className="club-what-to-expect">
                <h2>What To Expect</h2>
                <p>{club.whatToExpect}</p>
              </section>
            )}

            {club.whyWeLikeIt && (
              <section className="club-why-we-like-it">
                <h2>Why We Like It</h2>
                <p>{club.whyWeLikeIt}</p>
              </section>
            )}

            {club.howToJoin && (
              <section className="club-how-to-join">
                <h2>How To Join</h2>
                <p>{club.howToJoin}</p>
              </section>
            )}

            {/* Upcoming Events Section */}
            {club.events && club.events.length > 0 && (
              <section className="club-upcoming-events">
                <h2>Upcoming Events</h2>
                {club.events.map((event, index) => (
                  <div key={index} className="event-details">
                    <p><strong>Event:</strong> {event.name}</p>
                    <p><strong>Date:</strong> {event.date}</p>
                    <p><strong>Time:</strong> {event.time}</p>
                    {event.link && (
                      <a
                        href={event.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="event-link"
                      >
                        {event.linkText || "Register for the Event"}
                      </a>
                    )}
                  </div>
                ))}
              </section>
            )}

          </main>

          <aside className="club-sidebar">

            {club.runDays && club.runDays.length > 0 && (
              <section className="run-details">
                <h2>Run Details</h2>
                {/* <RunDetailsSharingComponent
                  clubTitle={club.title}
                  runDays={club.runDays}
                  clubId={club.id}
                /> */}
                <div className="run-days-container">
                  {club.runDays.map((runDay, index) => (
                    <div key={index} className="run-day-card">
                      <div className="run-day-header">
                        {runInfoIcons.day}
                        <span>{runDay.day}</span>
                      </div>
                      <div className="run-day-info">
                        {runDay.times && (
                          <div className="run-info-item">
                            {runInfoIcons.time}
                            <span>{Array.isArray(runDay.times) ? runDay.times.join(", ") : runDay.times}</span>
                          </div>
                        )}
                        {runDay.distance && (
                          <div className="run-info-item">
                            {runInfoIcons.distance}
                            <span>{runDay.distance}</span>
                          </div>
                        )}
                        {runDay.location && (
                          <div className="run-info-item">
                            {runInfoIcons.location}
                            <span>{runDay.location}</span>
                          </div>
                        )}
                        {runDay.details && (
                          <div className="run-info-item">
                            {runInfoIcons.details}
                            <span>{runDay.details}</span>
                          </div>
                        )}
                        {runDay.workoutType && (
                          <div className="run-info-item">
                            {runInfoIcons.workoutType}
                            <span>{runDay.workoutType}</span>
                          </div>
                        )}
                        {runDay.info && (
                          <div className="run-info-item">
                            {runInfoIcons.info}
                            <span>{runDay.info}</span>
                          </div>
                        )}
                      </div>
                      <div className="run-map">
                        {runDay.googleMapLink && <ClubMapRun googleMapLink={runDay.googleMapLink} />}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </aside>

        </div>  {/* closes club-content */}

      </div>  {/* closes club-main-layout-wrapper */}

    </div>  {/* closes styled div */}

    </div>
  );
}

export default ClubProfile;
